<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useProductCustomFields, getProductIdsFromLineItems } from '~/views/shop/composables/useProductCustomFields'

const { t } = useI18n()

const props = defineProps<{
  orderId: string
}>()

const isLoading = ref(true)
const {
  loadOrderDetails,
  shippingAddress,
  billingAddress,
  shippingMethod,
  order,
  subtotal,
  total,
  shippingCosts,
  paymentMethod,
} = useOrderDetails(props.orderId)
const { getFormattedPrice } = usePrice()
const { languageCode } = useLocale()
const { fetchCustomFields, isProductSoon } = useProductCustomFields()

onMounted(async () => {
  isLoading.value = true
  await loadOrderDetails()
  await fetchCustomFields(getProductIdsFromLineItems(order.value?.lineItems || []))
  isLoading.value = false
})

watch([languageCode], () => {
  loadOrderDetails()
})

const lineItems = computed(() => order.value?.lineItems || [])
</script>

<template>
  <div v-if="isLoading">Loading...</div>
  <div v-else>
    <h2 class="f-24">{{ t('shop.accountPage.ordersPage.detail.orderNumber') }} {{ order?.orderNumber }}</h2>
    <div class="details">
      <div>
        <h3 class="f-18">
          {{ t('shop.accountPage.ordersPage.detail.personalDetails') }}
        </h3>
        <p>
          {{ order?.orderCustomer?.firstName }} {{ order?.orderCustomer?.lastName }}<br />
          {{ order?.orderCustomer?.email }}
        </p>
      </div>
      <div>
        <h3 class="f-18">
          {{ t('shop.accountPage.ordersPage.detail.billingAddress') }}
        </h3>
        <p>
          {{ billingAddress?.street }} <br />
          {{ billingAddress?.zipcode }} {{ billingAddress?.city }} <br />
          {{ billingAddress?.phoneNumber }}
        </p>
      </div>
      <div>
        <h3 class="f-18">
          {{ t('shop.accountPage.ordersPage.detail.shippingAddress') }}
        </h3>
        <p>
          {{ shippingAddress?.street }} <br />
          {{ shippingAddress?.zipcode }} {{ shippingAddress?.city }} <br />
          {{ shippingAddress?.phoneNumber }}
        </p>
      </div>
      <div>
        <h3 class="f-18">
          {{ t('shop.accountPage.ordersPage.detail.paymentMethod') }}
        </h3>
        <p>
          {{ paymentMethod?.translated?.name }}
        </p>
      </div>
      <div>
        <h3 class="f-18">
          {{ t('shop.accountPage.ordersPage.detail.shippingMethod') }}
        </h3>
        <p>
          {{ shippingMethod?.translated?.name }}
        </p>
      </div>
    </div>
    <div v-if="lineItems.length" class="line-items">
      <div class="line-items-header f-bold">
        <div>{{ t('shop.accountPage.ordersPage.detail.product') }}</div>
        <div>{{ t('shop.accountPage.ordersPage.detail.price') }}</div>
        <div>{{ t('shop.accountPage.ordersPage.detail.quantity') }}</div>
        <div>{{ t('shop.accountPage.ordersPage.detail.amount') }}</div>
      </div>
      <div v-for="lineItem in lineItems" :key="lineItem.identifier" class="line-items-element">
        <div>
          <h5>{{ t('shop.accountPage.ordersPage.detail.product') }}</h5>
          <span>{{ lineItem.label }}</span>
          <span v-if="isProductSoon(lineItem.referencedId)" class="product-unavailable-label">
            ({{ t('shop.product.notYetPublished') }})
          </span>
        </div>
        <div>
          <h5>{{ t('shop.accountPage.ordersPage.detail.price') }}</h5>
          {{ getFormattedPrice(lineItem.unitPrice) }}
        </div>
        <div>
          <h5>{{ t('shop.accountPage.ordersPage.detail.quantity') }}</h5>
          {{ lineItem.quantity }}
        </div>
        <div>
          <h5>{{ t('shop.accountPage.ordersPage.detail.amount') }}</h5>
          {{ getFormattedPrice(lineItem.totalPrice) }}
        </div>
      </div>
    </div>
    <h4>{{ t('shop.accountPage.ordersPage.detail.summary') }}</h4>
    <ul class="cul">
      <li>
        <span class="name">{{ t('shop.accountPage.ordersPage.detail.subtotal') }}</span>
        <span class="value">{{ getFormattedPrice(subtotal) }}</span>
      </li>
      <li>
        <span class="name">{{ t('shop.accountPage.ordersPage.detail.shipping') }}</span>
        <span class="value">{{ getFormattedPrice(shippingCosts) }}</span>
      </li>
      <li>
        <span class="name f-bold">{{ t('shop.accountPage.ordersPage.detail.totalPrice') }}</span>
        <span class="value f-bold">{{ getFormattedPrice(total) }}</span>
      </li>
    </ul>
  </div>
</template>
<style scoped lang="scss">
h3,
h4,
p {
  margin-bottom: 0;
}
.details {
  display: grid;
  gap: var(--form-spacing);
  margin-bottom: var(--form-spacing);

  @include breakpoint(ts) {
    grid-template-columns: 1fr 1fr;
  }
}
.line-items {
  font-size: 18px;
  margin-bottom: var(--form-spacing);
  &-header,
  &-element {
    display: grid;
    border-top: 1px solid var(--c-line-light);
    padding: 10px 0;
    @include breakpoint(ts) {
      grid-template-columns: minmax(0, 3fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    }
    div {
      padding: 0 5px;
      margin-bottom: 20px;
      @include breakpoint(ts) {
        margin-bottom: 0;
      }
      &:last-child {
        padding-right: 5px;
        @include breakpoint(ts) {
          text-align: right;
        }
      }
    }
  }
  &-header {
    display: none;
    @include breakpoint(ts) {
      display: grid;
    }
  }
  &-element {
    &:hover {
      background: var(--c-background-light);
    }
    h5 {
      margin-bottom: 0;
      @include breakpoint(ts) {
        display: none;
      }
    }
  }
}
.cul {
  max-width: 500px;
  li {
    display: grid;
    gap: var(--form-spacing);
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    .value {
      text-align: right;
    }
  }
}
.product-unavailable-label {
  color: var(--c-text-muted, #666);
  font-style: italic;
  margin-left: 0.5rem;
}
</style>
